import { msalApp } from '../../auth';
import { getApigeeBearerToken } from './apigeeService';

/**
 * Returns a valid MSAL access token for the signed-in account.
 *
 * The chatbot stack is end-to-end MSAL/Azure AD: bot/auth.py validates
 * tokens against Microsoft Graph and api.py keys chat history on
 * `user_azure_oid` (a UUID derived from the Azure OID claim). The
 * WebSocket open path in WBCChatBot.js already uses
 * `msalApp.acquireTokenSilent(...)` for the same reason — this helper
 * mirrors that so REST calls and the WebSocket present the same identity
 * to the backend.
 *
 * Cognito-only users (external customers signed in via LoginPage) are
 * not chatbot users today: WBCChatBot.js:820 throws "No accounts found"
 * for them at the WebSocket open since `msalApp.getAllAccounts()` is
 * empty. Dual-mode auth (Cognito + MSAL) is tracked in MBBSERVT-337.
 */
const getAccessToken = async () => {
  const accounts = msalApp.getAllAccounts();
  console.log('[AUTH] MSAL accounts found:', accounts.length);
  if (accounts.length === 0) {
    throw new Error('No valid session. Please log in again.');
  }

  try {
    const response = await msalApp.acquireTokenSilent({
      scopes: ['email', 'openid', 'profile'],
      account: accounts[0],
    });
    console.log('[AUTH] MSAL token acquired for account:', accounts[0].username);
    return response.accessToken;
  } catch (err) {
    console.error('[AUTH] acquireTokenSilent failed:', err);
    throw new Error('No valid session. Please log in again.');
  }
};

/**
 * Authenticated fetch helper.
 *
 * Appends the MSAL access token as a `?token=<jwt>` query param. The
 * chatbot backend uses `token: str = Query(...)` on every authed REST
 * route (see wbc-chatbot/bot/api.py) — the same pattern the WebSocket
 * uses (`wss://.../api/v1/chat?token=...`). Sending the token as an
 * Authorization: Bearer header instead returns 422 because FastAPI
 * sees the required query param missing.
 *
 * Note: query-string tokens land in access logs and Referer headers.
 * MSAL access tokens are short-lived (~1h) so the blast radius is
 * bounded, but the long-term move is for the backend to accept either
 * the header or the query param. Tracked alongside MBBSERVT-337's
 * dual-mode work.
 */
export const fetchWithAuth = async (path, options = {}) => {
  const backendUrl =
    process.env.REACT_APP_CHAT_API_URL || 'https://wbc-server.dev.qualcomm.com';

  console.log('[AUTH] fetchWithAuth called for path:', path);

  try {
    console.log('[AUTH] Step 1: acquiring MSAL token');
    const token = await getAccessToken();
    console.log('[AUTH] Step 1 complete: MSAL token acquired');

    const separator = path.includes('?') ? '&' : '?';
    const fullUrl = `${backendUrl}${path}${separator}token=${encodeURIComponent(token)}`;
    console.log('[AUTH] Step 2: full request URL (token redacted):', fullUrl.replace(/token=[^&]+/, 'token=***'));

    console.log('[AUTH] Step 3: acquiring APIGEE bearer token');
    const apigeeToken = await getApigeeBearerToken();
    console.log('[AUTH] Step 3 complete: APIGEE bearer token acquired');

    console.log('[AUTH] Step 4: sending fetch request');
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${apigeeToken}`,
        'Content-Type': 'application/json',
      },
    });
    console.log('[AUTH] Step 4 complete: response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorBody = await response
        .json()
        .catch(() => ({ error: 'Failed to parse error response' }));
      console.error(
        `API Error: ${response.status} ${response.statusText}`,
        errorBody
      );
      throw new Error(`Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Failed to execute authenticated fetch to ${path}:`, error);
    throw error;
  }
};
